import moment from 'moment-timezone';

import dayjs from '@utils/dayjsInit';

import {
  DATE_PART_ORDER_DMY,
  DATE_PART_ORDER_MDY,
  DATE_PART_ORDER_YMD,
} from 'types/Global';
import type { Dayjs } from 'dayjs';
import type { DatePartOrder, DateTimeFormat, Global } from 'types';

/**
 * Global config shape for date/time formatting functions
 * Uses the Global type which matches the structure from global.json
 */
export type DateTimeGlobalConfig = Pick<Global, 'date_time_formats'>;

/**
 * Gets the appropriate date/time format object based on the date part ordering preference
 *
 * @param global - Global configuration object containing date format definitions
 * @param incomingDatePartOrder - Optional date part order override (MDY, DMY, YMD)
 * @returns DateTimeFormat object with format strings for the specified ordering
 */
export const getDateTimeFormatObject = (
  global: DateTimeGlobalConfig,
  incomingDatePartOrder?: DatePartOrder | string
): DateTimeFormat => {
  const defaultDatePartOrder = global.date_time_formats.default_date_part_order;
  const dateTimeFormatMap = global.date_time_formats.ordered_formats;

  const datePartOrder = (incomingDatePartOrder || defaultDatePartOrder) as DatePartOrder;

  return dateTimeFormatMap[datePartOrder];
};

/**
 * Formats a date/time string using user's locale preferences and timezone
 *
 * @param time - Date/time to format (ISO string, Date object, or moment-compatible input)
 * @param datePartOrder - User's date part ordering preference (MDY, DMY, YMD)
 * @param global - Global configuration object with date format definitions
 * @param dateFormatKey - Key for the date format to use (default: 'longSlashDate')
 * @param timeFormatKey - Key for the time format to use (default: 'hoursMinutesMeridiemWithTimezone')
 * @param dateOnly - If true, returns only the date portion without time (default: false)
 * @returns Formatted date/time string in user's locale and timezone
 *
 * @example
 * // Returns "12/17/2024, 3:30 PM (EST)" for MDY user
 * prettyDateTime('2024-12-17T15:30:00.000Z', 'MDY', global);
 *
 * @example
 * // Returns "17/12/2024, 3:30 PM (EST)" for DMY user
 * prettyDateTime('2024-12-17T15:30:00.000Z', 'DMY', global);
 *
 * @example
 * // Returns only date: "12/17/2024"
 * prettyDateTime('2024-12-17T15:30:00.000Z', 'MDY', global, 'longSlashDate', 'hoursMinutesMeridiemWithTimezone', true);
 */
export const prettyDateTime = (
  time: Date | moment.Moment | string,
  datePartOrder: DatePartOrder | string | undefined,
  global: DateTimeGlobalConfig,
  dateFormatKey: keyof DateTimeFormat = 'longSlashDate',
  timeFormatKey: keyof DateTimeFormat = 'hoursMinutesMeridiemWithTimezone',
  dateOnly = false
): string => {
  const dateTimeFormat = getDateTimeFormatObject(global, datePartOrder);
  const timezone = moment.tz.guess(true) || '';
  const timeInZone = moment.tz(time, timezone);
  const formattedDate = timeInZone.format(dateTimeFormat[dateFormatKey]);
  const formattedTime = timeInZone.format(dateTimeFormat[timeFormatKey]);

  let result = formattedDate;

  if (!dateOnly) {
    result += `, ${formattedTime}`;
  }

  return result;
};

// ---------------------------------------------------------------------------
// Fixed-format date-part-order formatting
// ---------------------------------------------------------------------------
//
// For displays whose date shape is fixed in the design (not sourced from the
// company-configurable `Global.date_time_formats`), still order the date's
// segments by the company's `DatePartOrder` preference. Callers own their own
// format strings and separator; this just applies the shared parse-guard,
// fallback, and compose logic.

export const formatTimestampByPartOrder = (
  isoTimestamp: string,
  datePartOrder: DatePartOrder | string | null | undefined,
  dateFormatByOrder: Record<string, string>,
  timeFormat: string,
  separator: string,
): string => {
  const inViewerZone = dayjs(isoTimestamp);

  if (!isoTimestamp || !inViewerZone.isValid()) return '';

  const dateFormat = dateFormatByOrder[String(datePartOrder)] ?? dateFormatByOrder[DATE_PART_ORDER_MDY];

  return `${inViewerZone.format(dateFormat)}${separator}${inViewerZone.format(timeFormat)}`;
};

// ---------------------------------------------------------------------------
// Date-input parsing service
// ---------------------------------------------------------------------------
//
// Purpose: turn user-facing date strings (URL params, form values, deep
// links) into a local-timezone `Dayjs` at start-of-day for the calendar day
// the user intended. Feature-agnostic - callers pass the raw string plus the
// tenant's date-part order (MDY / DMY / YMD).
//
// Accepted formats:
//   - Slash-separated  `M/D/YYYY`, `M/D/YY`, `MM/DD/YYYY`, etc.
//   - Dash-separated   `M-D-YYYY`, `M-D-YY`, `MM-DD-YYYY`, etc.
//
// The segment order (which is day / month / year) is driven entirely by the
// caller-supplied `DatePartOrder`. Separators must be consistent within a
// single string - mixed like `7-20/26` is rejected.
//
// Rules:
//   - Returned `Dayjs` values are in the browser's local timezone at
//     `.startOf('day')`. Callers that need the API-payload boundary should
//     apply `.endOf('day')` for the end of the range and `.toISOString()` at
//     the send site.
//   - Parsing is strict - dayjs's lenient rollover (e.g. `13/45/26` becoming
//     a valid date in some later month) is disabled by regex-shape gates and
//     strict format specifiers.
//   - Two-digit years follow dayjs's default `YY` pivot: 0-68 -> 20xx,
//     69-99 -> 19xx.

// A single string of digits-separator-digits-separator-digits, where both
// separators must be `/` OR both `-` (captured via backreference). Rejects
// mixed separators like `7-20/26`.
const DATE_SHAPE = /^\d{1,4}([/-])\d{1,4}\1\d{1,4}$/;

// Format strings per (order, year-width). Both `M/D/YYYY` and `MM/DD/YYYY`
// variants are tried so callers can freely mix padded/unpadded segments
// (`7/10/2026`, `07/10/2026`, `7/01/2026` all resolve). dayjs's strict mode
// disallows a `MM/DD/YYYY` input from matching a `M/D/YYYY` format string
// (and vice-versa), so we enumerate both variants and take the first hit.
const SLASH_FORMATS: Record<DatePartOrder, { long: string[]; short: string[] }> = {
  [DATE_PART_ORDER_DMY]: { long: ['D/M/YYYY', 'DD/MM/YYYY'], short: ['D/M/YY', 'DD/MM/YY'] },
  [DATE_PART_ORDER_MDY]: { long: ['M/D/YYYY', 'MM/DD/YYYY'], short: ['M/D/YY', 'MM/DD/YY'] },
  [DATE_PART_ORDER_YMD]: { long: ['YYYY/M/D', 'YYYY/MM/DD'], short: ['YY/M/D', 'YY/MM/DD'] },
};

// Normalizes the raw query-param value shape into a single non-empty string
// (or `undefined`). Next.js query params can arrive as `string`, `string[]`
// (repeated `?foo=a&foo=b`), or missing entirely; we take the first entry,
// trim whitespace, and treat empty strings as absent. Any non-string entry
// (shouldn't happen from URL query, but defensive) is treated as absent.
const firstIfArray = (raw: string[] | string | null | undefined): string | undefined => {
  if (raw == null) return undefined;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed || undefined;
};

// Parse a slash- or dash-separated date string against the given date-part
// order. Returns a local-tz `Dayjs` at start-of-day for the calendar day the
// user intended, or `null` on any parse failure (wrong shape, out-of-range
// month/day, mixed separators, empty input).
export const parseDateInput = (
  raw: string[] | string | null | undefined,
  order: DatePartOrder,
): Dayjs | null => {
  const value = firstIfArray(raw);
  if (!value || !DATE_SHAPE.test(value)) return null;

  // Normalize dashes to slashes so we reuse the single slash-format table.
  // The regex above already guaranteed the separator is consistent within
  // the string, so this substitution is safe.
  const slashed = value.replace(/-/g, '/');
  const parts = slashed.split('/');

  // Year segment position depends on the order: leading for YMD, trailing
  // otherwise. Its width (2 or 4 digits) picks the format table row.
  const yearSegment = order === DATE_PART_ORDER_YMD ? parts[0] : parts[2];
  const yearLen = yearSegment.length;
  if (yearLen !== 2 && yearLen !== 4) return null;

  const formats = yearLen === 4 ? SLASH_FORMATS[order].long : SLASH_FORMATS[order].short;
  const parsed = formats
    .map(format => dayjs(slashed, format, true))
    .find(candidate => candidate.isValid());
  return parsed ? parsed.startOf('day') : null;
};
